import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_URL, statusConfig, priorityConfig, formatRelativeTime } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Ticket, 
  Search, 
  Filter, 
  Plus,
  ChevronLeft,
  ChevronRight,
  User,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';

export default function TicketsPage() {
  const { user, isSolucionador } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [solucionadores, setSolucionadores] = useState([]);
  
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    priority: '',
    category_id: '',
    assignee_id: ''
  });

  const pageSize = 10;
  const totalPages = Math.ceil(total / pageSize);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('page_size', pageSize);
      
      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.category_id) params.append('category_id', filters.category_id);
      if (filters.assignee_id) params.append('assignee_id', filters.assignee_id);

      const response = await axios.get(`${API_URL}/tickets?${params.toString()}`);
      setTickets(response.data.tickets);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Error al cargar tickets');
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    fetchTickets();
    fetchCategories();
    if (isSolucionador) {
      fetchSolucionadores();
    }
  }, [page, filters, isSolucionador, fetchTickets]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchSolucionadores = async () => {
    try {
      const response = await axios.get(`${API_URL}/users/solucionadores`);
      setSolucionadores(response.data);
    } catch (error) {
      console.error('Error fetching solucionadores:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchTickets();
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: '',
      priority: '',
      category_id: '',
      assignee_id: ''
    });
    setPage(1);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tickets-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-gray-800">Tickets</h1>
          <p className="text-gray-500">{total} tickets encontrados</p>
        </div>
        <Button onClick={() => navigate('/tickets/new')} className="bg-[#7FD02E] hover:bg-[#6BC024] text-white shadow-sm" data-testid="new-ticket-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Ticket
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-gray-200 bg-white shadow-sm" data-testid="tickets-filters">
        <CardContent className="pt-6">
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por título o número..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <Button type="submit" variant="secondary" data-testid="search-btn">
                <Filter className="w-4 h-4 mr-2" />
                Buscar
              </Button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Select
                value={filters.status || "all"}
                onValueChange={(value) => setFilters({ ...filters, status: value === "all" ? "" : value })}
              >
                <SelectTrigger data-testid="filter-status">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  {Object.entries(statusConfig).map(([key, { label }]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filters.priority || "all"}
                onValueChange={(value) => setFilters({ ...filters, priority: value === "all" ? "" : value })}
              >
                <SelectTrigger data-testid="filter-priority">
                  <SelectValue placeholder="Prioridad" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las prioridades</SelectItem>
                  {Object.entries(priorityConfig).map(([key, { label }]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filters.category_id || "all"}
                onValueChange={(value) => setFilters({ ...filters, category_id: value === "all" ? "" : value })}
              >
                <SelectTrigger data-testid="filter-category">
                  <SelectValue placeholder="Categoría" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las categorías</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {isSolucionador && (
                <Select
                  value={filters.assignee_id || "all"}
                  onValueChange={(value) => setFilters({ ...filters, assignee_id: value === "all" ? "" : value })}
                >
                  <SelectTrigger data-testid="filter-assignee">
                    <SelectValue placeholder="Asignado a" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {solucionadores.map((sol) => (
                      <SelectItem key={sol.id} value={sol.id}>
                        {sol.first_name} {sol.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {(filters.status || filters.priority || filters.category_id || filters.assignee_id) && (
              <Button type="button" variant="ghost" size="sm" onClick={clearFilters}>
                Limpiar filtros
              </Button>
            )}
          </form>
        </CardContent>
      </Card>

      {/* Tickets Table */}
      <Card className="border-gray-200 bg-white shadow-sm" data-testid="tickets-table">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-pulse text-gray-500">Cargando tickets...</div>
            </div>
          ) : tickets.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Ticket className="w-12 h-12 text-gray-400 mb-4" />
              <p className="text-lg font-medium text-gray-800">No hay tickets</p>
              <p className="text-gray-500">Crea un nuevo ticket para comenzar</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-gray-100 hover:bg-transparent">
                  <TableHead className="text-gray-500">Ticket</TableHead>
                  <TableHead className="text-gray-500">Título</TableHead>
                  <TableHead className="text-gray-500">Estado</TableHead>
                  <TableHead className="text-gray-500">Prioridad</TableHead>
                  <TableHead className="text-gray-500 hidden lg:table-cell">Gerencia/Área</TableHead>
                  <TableHead className="text-gray-500 hidden md:table-cell">Asignado</TableHead>
                  <TableHead className="text-gray-500 hidden md:table-cell">Días restantes</TableHead>
                  <TableHead className="text-gray-500 hidden lg:table-cell">Creado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tickets.map((ticket) => (
                  <TableRow 
                    key={ticket.id} 
                    className="border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => navigate(`/tickets/${ticket.id}`)}
                    data-testid={`ticket-row-${ticket.id}`}
                  >
                    <TableCell>
                      <span className="font-mono text-sm text-[#7FD02E]">{ticket.ticket_number}</span>
                    </TableCell>
                    <TableCell>
                      <div className="max-w-xs">
                        <p className="font-medium truncate text-gray-800">{ticket.title}</p>
                        {ticket.category && (
                          <div className="flex items-center gap-1 mt-1">
                            <span 
                              className="w-2 h-2 rounded-full" 
                              style={{ backgroundColor: ticket.category.color }}
                            />
                            <span className="text-xs text-muted-foreground">{ticket.category.name}</span>
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={statusConfig[ticket.status]?.color}>
                        {statusConfig[ticket.status]?.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={priorityConfig[ticket.priority]?.color}>
                        {priorityConfig[ticket.priority]?.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {ticket.gerencia_area ? (
                        <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200">
                          {ticket.gerencia_area.name}
                        </Badge>
                      ) : (
                        <span className="text-gray-400 text-sm">—</span>
                      )}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {ticket.assignee ? (
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-[#7FD02E]/20 flex items-center justify-center">
                            <User className="w-3 h-3 text-[#7FD02E]" />
                          </div>
                          <span className="text-sm text-gray-700">{ticket.assignee.first_name}</span>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">Sin asignar</span>
                      )}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {(() => {
                        if (!ticket.due_date) {
                          return <span className="text-gray-400 text-sm">—</span>;
                        }
                        const isClosed = ['resuelto', 'cerrado'].includes(ticket.status);
                        const diff = Math.ceil((new Date(ticket.due_date) - new Date()) / (1000 * 60 * 60 * 24));
                        if (isClosed) {
                          return <span className="text-gray-400 text-sm">cerrado</span>;
                        }
                        if (diff < 0) {
                          return (
                            <Badge className="bg-rose-100 text-rose-700 border-rose-200">
                              Vencido {Math.abs(diff)}d
                            </Badge>
                          );
                        }
                        if (diff === 0) {
                          return (
                            <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                              Vence hoy
                            </Badge>
                          );
                        }
                        const color = diff <= 2
                          ? 'bg-amber-100 text-amber-700 border-amber-200'
                          : 'bg-emerald-100 text-emerald-700 border-emerald-200';
                        return (
                          <Badge className={color}>
                            {diff} día{diff !== 1 ? 's' : ''}
                          </Badge>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <div className="flex items-center gap-1 text-gray-500 text-sm">
                        <Clock className="w-3 h-3" />
                        {formatRelativeTime(ticket.created_at)}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between" data-testid="pagination">
          <p className="text-sm text-gray-500">
            Página {page} de {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              data-testid="prev-page-btn"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              data-testid="next-page-btn"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
